/** Set by the API proxy when auth cookies are wiped so the client can distinguish session death from resource 401s. */
export const SESSION_EXPIRED_HEADER = "x-session-expired";

const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/request-signup",
  "/complete-signup",
  "/forgot-password",
  "/update-forgotten-password",
  "/complete-login",
  "/enable-2fa",
  "/disable-2fa",
  "/super-admin/login",
];

export function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

interface SessionExpiredPayload {
  error_code?: string;
  message?: string;
  detail?: string;
}

/** True when the API response means the auth session is dead (not a resource-level 401). */
export function isSessionExpiredPayload(
  data: SessionExpiredPayload | null | undefined,
  headers?: Record<string, unknown> | Headers | null,
): boolean {
  const headerValue =
    headers instanceof Headers
      ? headers.get(SESSION_EXPIRED_HEADER) ?? headers.get("X-Session-Expired")
      : headers?.[SESSION_EXPIRED_HEADER] ?? headers?.["X-Session-Expired"] ?? headers?.["x-session-expired"];

  if (headerValue === "1" || headerValue === 1) {
    return true;
  }

  const errorCode = String(data?.error_code ?? "").toUpperCase();
  if (
    errorCode === "AUTHENTICATION_ERROR" ||
    errorCode === "NOT_AUTHENTICATED" ||
    errorCode === "SESSION_EXPIRED" ||
    errorCode === "TOKEN_EXPIRED"
  ) {
    return true;
  }

  const message = String(data?.message ?? data?.detail ?? "").trim();
  if (!message) return false;
  if (message === "Not authenticated") return true;
  return /session has expired|please log in again|not authenticated|invalid token|token expired/i.test(
    message,
  );
}

/**
 * Clear client auth state and send the user to the matching login page.
 * Safe to call from axios interceptors and fetch-based stream helpers.
 */
export function redirectToLoginOnSessionExpired(): void {
  // Use document so SSR/client bundles don't constant-fold the whole body away.
  if (typeof document === "undefined") return;
  if (typeof window === "undefined") return;
  if (isPublicAuthPath(window.location.pathname)) return;

  try {
    localStorage.clear();
  } catch {
    // ignore storage failures
  }

  const onSuperAdminApp = window.location.pathname.startsWith("/super-admin");
  window.location.href = onSuperAdminApp ? "/super-admin/login" : "/login";
}
